import { useCheckoutSessionOwnership } from "@circle-react/components/Paywalls/Checkout/contexts/CheckoutSessionOwnershipContext";
import {
  CHECKOUT_SESSION_OWNERSHIP_TOKEN_HEADER,
  type CheckoutSessionOwnershipToken,
} from "@circle-react/components/Paywalls/Checkout/sessionOwnership";
import { checkoutSession as checkoutSessionUrls } from "@circle-react/helpers/urlHelpers/internalApi/paywalls/checkout/sessions";
import { useMutation, useQueryClient } from "react-query";
import type { UseMutationResult } from "react-query";

import type { ApiError } from "@/react/config/CustomErrors";
import { reactQueryPost } from "@/react/helpers/backendRequestHelpers";
import type { CheckoutSession } from "@/react/types/Paywalls/Checkout/Session";

import { updateSessionCache } from "../../helpers/sessions/updateSessionCache";

interface CreateCheckoutSessionParams {
  eventId?: number;
  paywallId: number;
  referrer?: string;
  returnTo?: string;
}

export const useCreateCheckoutSession = (): UseMutationResult<
  CheckoutSession,
  ApiError,
  CreateCheckoutSessionParams
> => {
  const queryClient = useQueryClient();
  const { storeTokenForSession } = useCheckoutSessionOwnership();

  return useMutation({
    mutationFn: async (params: CreateCheckoutSessionParams) => {
      let ownershipToken: CheckoutSessionOwnershipToken | null = null;
      const session = await reactQueryPost<CheckoutSession>(
        checkoutSessionUrls.create(),
        {
          checkout_session: {
            paywall_id: params.paywallId,
            referrer: params.referrer,
            event_id: params.eventId,
            return_to: params.returnTo,
          },
        },
        {
          observeResponseHeader: {
            name: CHECKOUT_SESSION_OWNERSHIP_TOKEN_HEADER,
            onValue: value => {
              ownershipToken = value;
            },
          },
        },
      );

      if (ownershipToken) {
        storeTokenForSession(session.id, ownershipToken);
      }

      return session;
    },
    onSuccess: (
      newSession: CheckoutSession,
      variables: CreateCheckoutSessionParams,
    ) => {
      updateSessionCache(queryClient, variables.paywallId, newSession);
    },
    retry: 3,
  });
};
